/**
 * Centralized constants for Azure Web PubSub `inner.type` message strings.
 *
 * Existing command strings are documented here for discoverability but their
 * call-sites are left unchanged. New Canvas commands introduced in this file
 * are used by usePersistentChatGroups and CanvasPanelContext.
 */

export const PubSubCommand = {
  // ── Existing commands (documented, not yet migrated at call-sites) ──────────
  System: 'system',
  MessageReaction: 'message_reaction',
  AssistantThinking: 'assistant_thinking',

  /** Human participant typing presence (client-published, TTL-expired on receivers). */
  UserTyping: 'user_typing',

  // ── Canvas Panel control ─────────────────────────────────────────────────────
  /** Show the Canvas Panel (no document change). */
  CanvasShow: 'canvas_show',
  /** Hide/collapse the Canvas Panel. */
  CanvasHide: 'canvas_hide',
  /** Load a document into the Canvas Panel (also shows it). */
  CanvasLoadDocument: 'canvas_load_document',
} as const;

export type PubSubCommandType = (typeof PubSubCommand)[keyof typeof PubSubCommand];

// ── Canvas command payload types ────────────────────────────────────────────

export interface CanvasDocument {
  /** Publicly accessible URL or a signed blob URL. */
  uri: string;
  /** Display name shown in the panel header. */
  title?: string;
  /**
   * MIME type. If omitted the viewer infers from the file extension.
   * Examples: 'application/pdf', 'image/png'.
   */
  mimeType?: string;
  /** For PDFs: initial page to navigate to (1-based). */
  page?: number;
  /** For PDFs: snippet to search/highlight after load. */
  highlight?: string;
  /** Citation sourceId for provenance tracking. */
  sourceId?: string;
}

export interface CanvasShowCommand {
  type: typeof PubSubCommand.CanvasShow;
}

export interface CanvasHideCommand {
  type: typeof PubSubCommand.CanvasHide;
}

export interface CanvasLoadDocumentCommand {
  type: typeof PubSubCommand.CanvasLoadDocument;
  document: CanvasDocument;
}

export type CanvasCommand =
  | CanvasShowCommand
  | CanvasHideCommand
  | CanvasLoadDocumentCommand;

// ── Typing presence payload type ────────────────────────────────────────────

export interface UserTypingEvent {
  type: typeof PubSubCommand.UserTyping;
  collaborationId: string;
  isTyping: boolean;
  senderId: string;
  senderDisplayName: string;
  senderAvatarUrl?: string;
  timestamp: string;
}
